import React, { useRef, useEffect, useState, Suspense } from 'react';
import { ArrowLeft, MessageSquare, Check, CheckCheck, Loader2, Paperclip, FileText, X, SmilePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Message, Conversation } from '@/services/messageService';
import { formatMessageTime, formatMessageTimeDetailed, formatMessageTimeFull } from '@/utils/dateUtils';
import { format, isToday, isYesterday } from 'date-fns';
import { createDebouncedTypingIndicator, subscribeToTypingIndicators } from '@/services/typingService';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent, DrawerClose } from '@/components/ui/drawer';
import { EmojiClickData } from 'emoji-picker-react';
import { supabase } from '@/lib/supabase';
import { useResponsive } from '@/hooks/useResponsive';
import { RESPONSIVE_SPACING, RESPONSIVE_TYPOGRAPHY, TOUCH_TARGETS, RESPONSIVE_ANIMATIONS } from '@/design-tokens/responsive';
import { cn } from '@/lib/utils';

interface MessageThreadPanelProps {
  conversationId?: string;
  messages: Message[];
  newMessage: string;
  setNewMessage: (message: string) => void;
  currentConversation: Conversation | null;
  handleSendMessage: (message: string, document?: Document) => void;
  isMobile: boolean;
  onBack: () => void;
  currentUser: any;
}

interface MessageWithReadStatus extends Message {
  is_read?: boolean;
  read_at?: string;
}

interface TypingUser {
  user_id: string;
  is_typing: boolean;
}

// Utility to detect mobile devices
function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const MessageThreadPanel: React.FC<MessageThreadPanelProps> = ({
  conversationId,
  messages,
  newMessage,
  setNewMessage,
  currentConversation,
  handleSendMessage,
  isMobile: propIsMobile,
  onBack,
  currentUser
}) => {
  const { isMobile } = useResponsive();
  // Use responsive hook instead of prop for consistency
  const useMobile = isMobile;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [typingIndicator, setTypingIndicator] = useState<() => void | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);

  const isNonMobile = !useMobile;

  // Fetch the current parent's ID when component mounts
  useEffect(() => {
    const fetchCurrentParentId = async () => {
      if (!currentUser?.id) return;
      
      try {
        const { data: parentData, error } = await supabase
          .from('parents')
          .select('id')
          .eq('user_id', currentUser.id)
          .single();
          
        if (!error && parentData) {
          setCurrentParentId(parentData.id);
        }
      } catch (error) {
        console.error('Error fetching current parent ID:', error);
      }
    };
    
    fetchCurrentParentId();
  }, [currentUser]);

  // Set up the typing indicator when conversation changes
  useEffect(() => {
    if (!conversationId || !currentUser) return;
    
    // Create a debounced typing indicator for this user
    const debouncedTypingIndicator = createDebouncedTypingIndicator(
      conversationId,
      currentUser.id
    );
    
    setTypingIndicator(() => debouncedTypingIndicator);
    
    // Subscribe to typing indicators from other users
    const subscription = subscribeToTypingIndicators(
      conversationId,
      (users) => {
        // Filter out current user from typing indicators
        const otherTypingUsers = users.filter(user => user.user_id !== currentUser.id);
        setTypingUsers(otherTypingUsers);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId, currentUser]);

  // Auto-scroll to bottom when new messages come in or typing status changes
  useEffect(() => {
    // Only scroll on desktop/tablet
    if (!useMobile && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typingUsers]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      handleSendMessage(newMessage);
      setNewMessage('');
      // Close drawer on mobile after sending
      if (useMobile) {
        setDrawerOpen(false);
        // Optional: Navigate back to conversation list on mobile after sending
        // Uncomment the next line if you want to go back to conversation list after sending
        // setTimeout(() => onBack(), 500);
      }
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    // Trigger typing indicator
    if (typingIndicator) {
      typingIndicator();
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const cursorPosition = inputRef.current?.selectionStart || newMessage.length;
    const updatedMessage = 
      newMessage.substring(0, cursorPosition) + 
      emoji + 
      newMessage.substring(cursorPosition);
    setNewMessage(updatedMessage);
    // Focus back on textarea after selecting emoji
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.selectionStart = cursorPosition + emoji.length;
        inputRef.current.selectionEnd = cursorPosition + emoji.length;
      }
    }, 10);
  };

  const isCurrentUserMessage = (message: MessageWithReadStatus) => {
    // For parent messages, compare the sender_id with the current parent's ID
    if (message.sender_type === 'parent' && currentParentId) {
      return message.sender_id === currentParentId;
    }
    return false;
  };

  const formatTimestamp = (timestamp: string) => {
    return formatMessageTime(timestamp);
  };

  // Helper to format date separator - recalculate each time to avoid sticky dates
  const getDateSeparator = (date: Date) => {
    const now = new Date();
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  };

  // Helper to format time for each message using centralized function
  const getMessageTime = (timestamp: string) => {
    return formatMessageTimeDetailed(timestamp);
  };

  // Helper to format full timestamp using centralized function
  const getFullTimestamp = (timestamp: string) => {
    return formatMessageTimeFull(timestamp);
  };

  return (
    <div className="flex flex-col h-full w-full border rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* Thread header */}
      <div className={cn(
        "flex items-center border-b bg-gray-50/50",
        RESPONSIVE_SPACING.card.padding
      )}>
        {useMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack} 
            className="mr-2 hover:bg-gray-100"
            style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
            aria-label="Back to messages"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex items-center">
          <MessageSquare className="h-5 w-5 mr-3 text-primary" />
          <div>
            <h3 className={cn(
              "font-semibold text-gray-900",
              RESPONSIVE_TYPOGRAPHY.h4
            )}>
              {currentConversation?.title || 'Conversation'}
            </h3>
            {currentConversation?.family && (
              <p className={cn(
                "text-muted-foreground",
                RESPONSIVE_TYPOGRAPHY.caption
              )}>
                {currentConversation.family.name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Messages area */}
      {isNonMobile ? (
        <div className={cn(
          "flex-1 overflow-y-auto space-y-4 bg-slate-50 flex flex-col justify-end",
          RESPONSIVE_SPACING.section.mobile
        )}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <MessageSquare className="h-16 w-16 mb-4 opacity-20" />
              <p className={RESPONSIVE_TYPOGRAPHY.bodyLarge}>No messages yet</p>
              <p className={RESPONSIVE_TYPOGRAPHY.bodySmall}>Start the conversation by sending a message below</p>
            </div>
          ) : (
            (() => {
              let lastDate: string | null = null;
              return (
                <>
                  {messages.map((message, idx) => {
                    const msgDate = new Date(message.created_at);
                    const dateStr = format(msgDate, 'yyyy-MM-dd');
                    const showDateSeparator = lastDate !== dateStr;
                    lastDate = dateStr;
                    return (
                      <React.Fragment key={message.id}>
                        {showDateSeparator && (
                          <div className="flex justify-center my-4">
                            <span className={cn(
                              "px-4 py-2 rounded-full bg-gray-200 text-gray-600 font-medium",
                              RESPONSIVE_TYPOGRAPHY.caption
                            )}>
                              {getDateSeparator(msgDate)}
                            </span>
                          </div>
                        )}
                        <div
                          className={`flex ${
                            isCurrentUserMessage(message as MessageWithReadStatus) ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-2xl shadow-sm",
                              RESPONSIVE_SPACING.card.padding,
                              isCurrentUserMessage(message as MessageWithReadStatus)
                                ? 'bg-primary text-white shadow-primary/20'
                                : 'bg-white border border-gray-200 text-gray-900'
                            )}
                          >
                            <div className={cn(
                              "leading-relaxed",
                              RESPONSIVE_TYPOGRAPHY.body
                            )}>
                              {message.content}
                            </div>
                            <div className={cn(
                              "flex items-center justify-between mt-2 opacity-70",
                              RESPONSIVE_TYPOGRAPHY.caption
                            )}>
                              {/* Time with tooltip for full timestamp */}
                              <span
                                className="text-muted-foreground cursor-help"
                                title={getFullTimestamp(message.created_at)}
                              >
                                {getMessageTime(message.created_at)}
                              </span>
                              {isCurrentUserMessage(message as MessageWithReadStatus) && (
                                <span className="flex items-center ml-2">
                                  {(message as MessageWithReadStatus).is_read 
                                    ? <CheckCheck className="h-3 w-3 ml-1" /> 
                                    : <Check className="h-3 w-3 ml-1" />
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()
          )}
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <div className={cn(
              "flex items-center space-x-2 text-gray-500 py-2",
              RESPONSIVE_TYPOGRAPHY.caption
            )}>
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>
                {typingUsers.length === 1 ? 'Someone is typing...' : 'Multiple people are typing...'}
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      ) : (
        <div className={cn(
          "flex-1 overflow-y-auto space-y-4 bg-slate-50",
          RESPONSIVE_SPACING.section.mobile
        )}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <MessageSquare className="h-16 w-16 mb-4 opacity-20" />
              <p className={RESPONSIVE_TYPOGRAPHY.bodyLarge}>No messages yet</p>
              <p className={RESPONSIVE_TYPOGRAPHY.bodySmall}>Start the conversation by sending a message below</p>
            </div>
          ) : (
            (() => {
              let lastDate: string | null = null;
              return (
                <>
                  {messages.map((message, idx) => {
                    const msgDate = new Date(message.created_at);
                    const dateStr = format(msgDate, 'yyyy-MM-dd');
                    const showDateSeparator = lastDate !== dateStr;
                    lastDate = dateStr;
                    return (
                      <React.Fragment key={message.id}>
                        {showDateSeparator && (
                          <div className="flex justify-center my-4">
                            <span className={cn(
                              "px-4 py-2 rounded-full bg-gray-200 text-gray-600 font-medium",
                              RESPONSIVE_TYPOGRAPHY.caption
                            )}>
                              {getDateSeparator(msgDate)}
                            </span>
                          </div>
                        )}
                        <div
                          className={`flex ${
                            isCurrentUserMessage(message as MessageWithReadStatus) ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-2xl shadow-sm",
                              RESPONSIVE_SPACING.card.padding,
                              isCurrentUserMessage(message as MessageWithReadStatus)
                                ? 'bg-primary text-white shadow-primary/20'
                                : 'bg-white border border-gray-200 text-gray-900'
                            )}
                          >
                            <div className={cn(
                              "leading-relaxed",
                              RESPONSIVE_TYPOGRAPHY.body
                            )}>
                              {message.content}
                            </div>
                            <div className={cn(
                              "flex items-center justify-between mt-2 opacity-70",
                              RESPONSIVE_TYPOGRAPHY.caption
                            )}>
                              {/* Time with tooltip for full timestamp */}
                              <span
                                className="text-muted-foreground cursor-help"
                                title={getFullTimestamp(message.created_at)}
                              >
                                {getMessageTime(message.created_at)}
                              </span>
                              {isCurrentUserMessage(message as MessageWithReadStatus) && (
                                <span className="flex items-center ml-2">
                                  {(message as MessageWithReadStatus).is_read 
                                    ? <CheckCheck className="h-3 w-3 ml-1" /> 
                                    : <Check className="h-3 w-3 ml-1" />
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()
          )}
          {/* Typing indicator */}
          {typingUsers.length > 0 && (
            <div className={cn(
              "flex items-center space-x-2 text-gray-500 py-2",
              RESPONSIVE_TYPOGRAPHY.caption
            )}>
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span>
                {typingUsers.length === 1 ? 'Someone is typing...' : 'Multiple people are typing...'}
              </span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Message input */}
      {useMobile ? (
        <>
          {/* Large, inviting input bar at the bottom */}
          <div 
            className={cn(
              "border-t bg-white flex items-center cursor-pointer",
              RESPONSIVE_SPACING.card.padding
            )}
            onClick={() => setDrawerOpen(true)}
          >
            <div className={cn(
              "flex-1 rounded-full border bg-slate-50 px-4 py-3 text-muted-foreground flex items-center",
              RESPONSIVE_TYPOGRAPHY.body
            )}
            style={{ minHeight: TOUCH_TARGETS.comfortable }}
            >
              {newMessage ? newMessage.slice(0, 32) + (newMessage.length > 32 ? '…' : '') : 'Type a message…'}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-3 hover:bg-gray-100"
              style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
              title="Compose message"
            >
              <SmilePlus className="h-5 w-5" />
            </Button>
          </div>
          {/* Drawer for message composer */}
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerContent className="pb-safe bg-white border-t rounded-t-2xl">
              <div className="flex justify-between items-center px-4 pt-4 pb-2">
                <span className="font-semibold text-lg">New Message</span>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </DrawerClose>
              </div>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-2 px-4 pb-4"
              >
                <Textarea
                  ref={inputRef}
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={handleInputChange}
                  rows={4}
                  className="resize-none min-h-[64px] max-h-40 text-base rounded-lg border px-3 py-2"
                  aria-label="Message input"
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        title="Add emoji"
                      >
                        <SmilePlus className="h-5 w-5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto" align="end">
                      <Suspense fallback={<div style={{ width: 300, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>}>
                        <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                      </Suspense>
                    </PopoverContent>
                  </Popover>
                  <Button type="submit" disabled={!newMessage.trim()}>
                    Send
                  </Button>
                </div>
              </form>
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        // Desktop: enhanced inline input
        <form onSubmit={handleSubmit} className={cn(
          "border-t bg-white",
          RESPONSIVE_SPACING.card.padding
        )}>
          <div className="flex items-center gap-3">
            <Textarea
              ref={inputRef}
              placeholder="Type your message..."
              value={newMessage}
              onChange={handleInputChange}
              rows={2}
              className={cn(
                "flex-1 resize-none max-h-32 rounded-xl border-gray-200 focus:border-primary focus:ring-primary/20",
                RESPONSIVE_TYPOGRAPHY.body
              )}
              style={{ minHeight: TOUCH_TARGETS.minSize }}
              aria-label="Message input"
            />
            {/* Emoji Picker with Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="hover:bg-gray-50"
                  style={{ minHeight: TOUCH_TARGETS.minSize, minWidth: TOUCH_TARGETS.minSize }}
                  title="Add emoji"
                >
                  <SmilePlus className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="end">
                <Suspense fallback={<div style={{ width: 300, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>}>
                  <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                </Suspense>
              </PopoverContent>
            </Popover>
            <Button 
              type="submit" 
              disabled={!newMessage.trim()}
              style={{ minHeight: TOUCH_TARGETS.minSize }}
            >
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MessageThreadPanel;
//comment